import { Trans } from '@lingui/macro'
import { Grid } from '@mui/material'
import Typography from '@mui/material/Typography'
import './FormObjectivesUpdateReadOnly.css'
import { useSelector } from 'react-redux'
import { lastReportTitle, previousReportTitle } from './FormObjectivesUpdateUtils'
import { bilingualDateFormat } from 'app/views/common/Formats'

/** Component to render a list of objective updates
 * @component
 * @param {string} title - The title of the list
 * @param {array} value - values of objective updates
 * @returns {React.Component}
 */
export const FormObjectivesUpdateReadOnly = ({
  title,
  value,
  connectedObject
}) => {
  const language = useSelector((state) => state.user.language) || 'en'

  const latestReport = connectedObject?.latestReport
  const latestObjectiveUpdates =
    latestReport?.objectiveUpdates || []

  return (
    <div className='form-objectives-update-read-only'>
      {/* <div className='description-container'>
        <Typography className='description-text'>
          <Trans>FORM_OBJECTIVES_UPDATE_TOP_INFO_LABEL</Trans>
        </Typography>
      </div> */}

      {/* <div className='form-print-title'>{title}</div> */}
      {title && (
        <div className='form-print-title'>{title}</div>
      )}
      <div className='container'>
        {value.map((data, index) => {
          const {
            objective,
            needs,
            id,
            outcomes = [],
            objectivesUpdate = {}
          } = data
          const isLast = index === value.length - 1
          const identifiedNeedChanges = [objectivesUpdate]
            .filter((update) => update.identifiedNeedChanges)
            .map((update) => update.identifiedNeedChanges)
          const actualOutcomes = [objectivesUpdate]
            .filter((update) => update.actualOutcomes)
            .map((update) => update.actualOutcomes)
          const desiredOutcomeChanges = [objectivesUpdate]
            .filter((update) => update.desiredOutcomeChanges)
            .map((update) => update.desiredOutcomeChanges)
          return (
            <div key={index} className={isLast ? '' : 'section-border'}>
              <div className='top-header section section-border'>
                <Typography className='text-name'>
                  <Trans>Objective</Trans>
                </Typography>
                <Typography className='text'>{objective}</Typography>
              </div>

              <div className='section needs-outcomes-section section-border'>
                <div item className='section-name-column'>
                  <Typography className='text-name'>
                    <Trans>Needs</Trans>
                  </Typography>
                </div>

                <div className='outcomes-field-columns'>
                  <div className='answers'>
                    <Typography className='text'>
                      {needs}
                    </Typography>
                    {latestObjectiveUpdates
                      .filter(
                        (answer) =>
                          answer.identifiedNeedChanges &&
                          answer.objectiveId === id
                      )
                      .map((answer, idx) => {
                        const title =
                          idx === 0
                            ? lastReportTitle
                            : previousReportTitle
                        const formattedDate = bilingualDateFormat(
                          latestReport.submissionDate,
                          language
                        )
                        return (
                          <div key={idx} className='answer'>
                            <Typography className='text-report'>
                              {title}: {formattedDate}
                            </Typography>
                            <Typography className='text'>
                              {answer.identifiedNeedChanges}
                            </Typography>
                          </div>
                        )
                      })}
                  </div>

                  <div className='answer'>
                    <Typography
                      sx={{ color: 'primary.main' }}
                      className='question-text'
                    >
                      <Trans>
                        FORM_OBJECTIVES_UPDATE_IDENTIFIED_NEEDS_CHANGES
                      </Trans>
                    </Typography>
                    <Typography className='text'>
                      {identifiedNeedChanges}
                    </Typography>
                  </div>
                </div>
              </div>

              <div className='section needs-outcomes-section'>
                <Grid item className='section-name-column'>
                  <Typography className='text-name'>
                    <Trans>Outcomes</Trans>
                  </Typography>
                </Grid>

                <div className='outcomes-field-columns'>
                  <div className='answers'>
                    <div className='answer'>
                      {outcomes.map((outcome, idx) => {
                        return (
                          <div key={idx}>
                            <Typography className='text'>
                              {idx + 1}. {outcome}
                            </Typography>
                          </div>
                        )
                      })}
                    </div>

                    {latestObjectiveUpdates
                      .filter(
                        (answer) =>
                          answer.desiredOutcomeChanges &&
                          answer.objectiveId === id
                      )
                      .map((answer, idx) => {
                        const title =
                          idx === 0
                            ? lastReportTitle
                            : previousReportTitle
                        const formattedDate = bilingualDateFormat(
                          latestReport.submissionDate,
                          language
                        )
                        return (
                          <Grid
                            key={idx}
                            direction='column'
                            className='answer'
                          >
                            <Typography className='text-report'>
                              {title}: {formattedDate}
                            </Typography>
                            <Typography>
                              {answer.desiredOutcomeChanges}
                            </Typography>
                          </Grid>
                        )
                      })}

                  </div>

                  <div className='answer'>
                    <Typography
                      sx={{ color: 'primary.main' }}
                      className='question-text'
                    >
                      <Trans>
                        FORM_OBJECTIVES_UPDATE_DESIRED_OUTCOME_CHANGES
                      </Trans>
                    </Typography>
                    <Typography className='text'>
                      {desiredOutcomeChanges}
                    </Typography>
                  </div>
                </div>
              </div>

              <div className='section needs-outcomes-section divider-container'>
                <div className='section-name-column' />
                <div className='divider' />
              </div>

              <div className='needs-outcomes-section section'>
                <div className='section-name-column' />
                <div className='outcomes-field-columns'>
                  <Grid item container xs className='answers'>
                    {latestObjectiveUpdates
                      .filter(
                        (answer) =>
                          answer.actualOutcomes &&
                          answer.objectiveId === id
                      )
                      .map((answer, idx) => {
                        const title =
                          idx === 0
                            ? lastReportTitle
                            : previousReportTitle
                        const formattedDate = bilingualDateFormat(
                          latestReport.submissionDate,
                          language
                        )

                        return (
                          <Grid
                            key={idx}
                            direction='column'
                            className='answer'
                          >
                            <Typography className='text-report'>
                              {title}: {formattedDate}
                            </Typography>
                            <Typography>
                              {answer.actualOutcomes}
                            </Typography>
                          </Grid>
                        )
                      })}
                  </Grid>

                  <div className='answer'>
                    <Typography
                      sx={{ color: 'primary.main' }}
                      className='question-text'
                    >
                      <Trans>
                        FORM_OBJECTIVES_UPDATE_ACTUAL_OUTCOMES
                      </Trans>
                    </Typography>
                    <Typography className='text'>
                      {actualOutcomes}
                    </Typography>
                  </div>
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
